import React, { useState } from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  IconButton,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CheckIcon from '@mui/icons-material/Check';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, Language } from '../context/LanguageContext';
import siteConfig from '../config/siteConfig';

const Header = () => {
  const theme = useTheme();
  const { language, setLanguage, translations } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = (lang?: Language) => {
    if (lang) {
      setLanguage(lang);
    }
    setAnchorEl(null);
  };

  const languages = [
    { code: 'mr' as Language, label: 'मराठी' },
    { code: 'hi' as Language, label: 'हिंदी' },
    { code: 'en' as Language, label: 'English' },
  ];

  return (
    <Box 
      component="header" 
      sx={{
        background: '#00106e',
        color: 'white',
        py: 2,
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          {/* Logo and Title */}
          <Stack 
            direction="row" 
            alignItems="center" 
            spacing={2}
            component={Link}
            to="/"
            sx={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Box
              component="img"
              src="/nationalEmblem2.png"
              alt="Kirksal Grampanchayat Logo"
              sx={{
                width: { xs: 50, md: 60 },
                height: { xs: 50, md: 60 },
                filter: 'brightness(0) invert(1)',
              }}
            />
            <Box>
              <Typography 
                variant="h4" 
                component="h1"
                sx={{
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                {siteConfig.title}
              </Typography>
              <Typography 
                variant="body2"
                sx={{
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  opacity: 0.9,
                }}
              >
                {language === 'mr' ? 'महाराष्ट्र शासन' :
                 language === 'hi' ? 'महाराष्ट्र सरकार' :
                 'Government of Maharashtra'}
              </Typography>
            </Box>
          </Stack>

          {/* Right Side Controls */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              '& .MuiIconButton-root': {
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              display: { xs: 'none', md: 'flex' },
            }}
          >
            {/* Weather Widget */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <WbSunnyIcon />
              <Typography variant="body2">32°C Kirksal</Typography>
            </Stack>

            {/* Language Toggle */}
            <IconButton 
              aria-label="change language"
              onClick={handleLanguageClick}
              sx={{ 
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              <TranslateIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => handleLanguageClose()}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                },
              }}
            >
              {languages.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => handleLanguageClose(lang.code)}
                  selected={language === lang.code}
                >
                  <ListItemIcon>
                    {language === lang.code && <CheckIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText>{lang.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>

            {/* Accessibility */}
            <IconButton aria-label="accessibility options">
              <AccessibilityNewIcon />
            </IconButton>

            {/* Emergency Contact */}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {translations.emergency[language]}: 112
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Header;
